// App.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import botAvatar from "./bot.svg";
import userAvatar from "./user.svg";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import CircularProgress from "@mui/material/CircularProgress";
import { db } from "./firebase";
import ChatInput from "./components/chatInput";
import MessagesList from "./components/MessagesList";
import HeadBar from "./components/HeadBar";
import { useWebSocket } from "./hooks/useWebSocket";
import { useChat } from "./hooks/useChat";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
// Importa `useTheme` per accedere alla palette
import { useTheme } from '@mui/material/styles';
import { 
  getOrCreateGuestId, 
  isGuestUser, 
  hasReachedGuestLimit 
} from './utils/guestUtils';
import BoatConnectAuthService from './services/boatConnectAuth';

export const formatTime = (date: Date) =>
  date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

interface AppProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

function App({ darkMode, toggleDarkMode }: AppProps) {
  const [resetKey, setResetKey] = useState(0); 
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const carouselRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [showScrollFab, setShowScrollFab] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isResponding, setIsResponding] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  
  // Guest user limit states
  const [guestLimitReached, setGuestLimitReached] = useState(false);
  const GUEST_MESSAGE_LIMIT = 1000;
  
  // Nuovi stati per gestire l'auto-scroll intelligente
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollPositionRef = useRef(0);
  const scrollVelocityRef = useRef(0);
  const isStreamingRef = useRef(false);
  
  // Get session_id from URL query parameters
  const sessionId = new URLSearchParams(window.location.search).get('session_id');
  
  // Helper functions for userId persistence in localStorage
  const STORAGE_KEY_USER_ID = 'boatconnect_user_id';
  const STORAGE_KEY_USER_FULL_NAME = 'boatconnect_user_full_name';
  const STORAGE_KEY_USER_EMAIL = 'boatconnect_user_email';
  
  const saveUserId = (id: string) => {
    try {
      localStorage.setItem(STORAGE_KEY_USER_ID, id);
      console.log(`💾 Saved userId to localStorage: ${id}`);
    } catch (error) {
      console.error("❌ Failed to save userId to localStorage:", error);
    }
  };
  
  const loadUserId = (): string | null => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_USER_ID);
      if (saved) {
        console.log(`📂 Loaded userId from localStorage: ${saved}`);
      }
      return saved;
    } catch (error) {
      console.error("❌ Failed to load userId from localStorage:", error);
      return null;
    }
  };

  const saveAuthenticatedProfile = (fullName?: string, email?: string) => {
    try {
      if (fullName && fullName.trim()) {
        localStorage.setItem(STORAGE_KEY_USER_FULL_NAME, fullName.trim());
      } else {
        localStorage.removeItem(STORAGE_KEY_USER_FULL_NAME);
      }

      if (email && email.trim()) {
        localStorage.setItem(STORAGE_KEY_USER_EMAIL, email.trim());
      } else {
        localStorage.removeItem(STORAGE_KEY_USER_EMAIL);
      }
    } catch (error) {
      console.error("❌ Failed to save authenticated profile to localStorage:", error);
    }
  };

  const clearAuthenticatedProfile = () => {
    try {
      localStorage.removeItem(STORAGE_KEY_USER_FULL_NAME);
      localStorage.removeItem(STORAGE_KEY_USER_EMAIL);
    } catch (error) {
      console.error("❌ Failed to clear authenticated profile from localStorage:", error);
    }
  };

  const loadAuthenticatedProfile = () => {
    try {
      return {
        userFullName: localStorage.getItem(STORAGE_KEY_USER_FULL_NAME),
        userEmail: localStorage.getItem(STORAGE_KEY_USER_EMAIL),
      };
    } catch (error) {
      console.error("❌ Failed to load authenticated profile from localStorage:", error);
      return {
        userFullName: null,
        userEmail: null,
      };
    }
  };
  
  // User ID - will be set after session verification or loaded from localStorage
  const [userId, setUserId] = useState<string | null>(() => {
    // Initialize from localStorage on mount
    return loadUserId();
  });
  const [userFullName, setUserFullName] = useState<string | null>(() => {
    const profile = loadAuthenticatedProfile();
    return profile.userFullName;
  });
  const [userEmail, setUserEmail] = useState<string | null>(() => {
    const profile = loadAuthenticatedProfile();
    return profile.userEmail;
  });
  const [isVerifyingSession, setIsVerifyingSession] = useState<boolean>(!!sessionId);
  
  // Ref to prevent double verification in React Strict Mode
  const hasVerifiedRef = useRef(false);

  // Verify session and get userId on mount using Boat Connect Auth Service
  useEffect(() => {
    // Prevent double verification (React Strict Mode mounts twice in dev)
    if (hasVerifiedRef.current) {
      console.log("⏭️ Skipping duplicate verification (already verified)");
      return;
    }
    
    const verifySession = async () => {
      // If we have a saved userId and no session_id in URL, use the saved one
      if (!sessionId && userId) {
        console.log(`♻️ Using saved userId from localStorage: ${userId}`);
        setIsVerifyingSession(false);
        
        // Check if it's a guest and has reached limit
        if (isGuestUser(userId) && hasReachedGuestLimit(userId, GUEST_MESSAGE_LIMIT)) {
          setGuestLimitReached(true);
          setUserFullName(null);
          setUserEmail(null);
          clearAuthenticatedProfile();
        }
        hasVerifiedRef.current = true;
        return;
      }
      
      // If no session_id, create/use guest user
      if (!sessionId) {
        console.warn("⚠️ No session_id found in URL, using guest user");
        const guestId = getOrCreateGuestId();
        setUserId(guestId);
        setUserFullName(null);
        setUserEmail(null);
        clearAuthenticatedProfile();
        saveUserId(guestId);
        setIsVerifyingSession(false);
        
        // Check if guest has reached limit
        if (hasReachedGuestLimit(guestId, GUEST_MESSAGE_LIMIT)) {
          setGuestLimitReached(true);
        }
        hasVerifiedRef.current = true;
        return;
      }

      // Session ID present, verify before setting userId
      setIsVerifyingSession(true);
      
      try {
        console.log("🔄 Verifying session before connecting...");
        
        // Use Boat Connect Auth Service to verify session
        const data = await BoatConnectAuthService.verifySession(sessionId);
        
        if (data.valid && data.user_id) {
          console.log(`✅ Session valid, setting userId to: ${data.user_id}`);
          setUserId(data.user_id);
          saveUserId(data.user_id);
          const authenticatedFullName = data.user_full_name?.trim() || null;
          const authenticatedEmail = data.user_email?.trim() || null;
          setUserFullName(authenticatedFullName);
          setUserEmail(authenticatedEmail);
          saveAuthenticatedProfile(authenticatedFullName || undefined, authenticatedEmail || undefined);
          // Authenticated users have no message limit
          setGuestLimitReached(false);
          
          // Clean session_id from URL after successful verification
          BoatConnectAuthService.cleanSessionIdFromUrl();
        } else {
          console.warn("⚠️ Invalid session, falling back to guest user");
          
          const guestId = getOrCreateGuestId();
          setUserId(guestId);
          setUserFullName(null);
          setUserEmail(null);
          clearAuthenticatedProfile();
          saveUserId(guestId);
          
          // Check if guest has reached limit
          if (hasReachedGuestLimit(guestId, GUEST_MESSAGE_LIMIT)) {
            setGuestLimitReached(true);
          }
        }
      } catch (error) {
        console.error("❌ Error during session verification:", error);
        console.warn("🔄 Falling back to guest user");
        
        const guestId = getOrCreateGuestId();
        setUserId(guestId);
        setUserFullName(null);
        setUserEmail(null);
        clearAuthenticatedProfile();
        saveUserId(guestId);
        
        // Check if guest has reached limit
        if (hasReachedGuestLimit(guestId, GUEST_MESSAGE_LIMIT)) {
          setGuestLimitReached(true);
        }
      } finally {
        setIsVerifyingSession(false);
        hasVerifiedRef.current = true;
      }
    };

    verifySession();
  }, [sessionId, userId]);

  // Build WebSocket URL with userId in path
  const buildWsUrl = useCallback(() => {
    // Don't build URL if userId is not ready yet
    if (!userId) return null;
    
    const host = process.env.REACT_APP_SERVER_HOST || process.env.SERVER_HOST || "18.212.35.6";
    const port = process.env.REACT_APP_SERVER_PORT || process.env.SERVER_PORT || "80";
    const isProd = process.env.NODE_ENV === "production";
    const usingHttps = window.location.protocol === "https:";
    const protocol = isProd || usingHttps ? "wss" : "ws";
    
    let wsUrl = `${protocol}://${host}`;
    if (!(isProd && (port === "80" || port === "443"))) {
      wsUrl += `:${port}`;
    }
    // Include userId in WebSocket path: ws://host:port/ws/{userId}
    wsUrl += `/ws/${userId}`;

    // Add API Key if present
    const wsApiKey = process.env.REACT_APP_API_KEY_WS;
    if (wsApiKey) {
      wsUrl += `?token=${wsApiKey}`;
    }
    
    return wsUrl;
  }, [userId]);

  const wsUrl = userId ? buildWsUrl() : null;
  
  const timeoutIdRef = useRef<number | null>(null);

  // Ottieni il tema corrente dall'hook useTheme
  const theme = useTheme();

  const handleResponseEnd = useCallback(() => {
    console.log("[APP] Response ended");
    setIsResponding(false);
    setShowTypingIndicator(false);
    isStreamingRef.current = false;
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const handleSocketError = useCallback(() => {
    console.log("WebSocket connection error or close detected.");
    setShowErrorCard(true);
    timeoutIdRef.current && window.clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = null;
  }, []);

  const {
    messages,
    isStreaming,
    isTyping,
    hasMore,
    inputDisabled,
    loadMessages,
    loadMoreMessages,
    clearFirestoreMessages,
    pendingResponses,
    handleMessage,
    sendMessage: sendChatMessage,
  } = useChat({ userId: userId || "", db, onResponseEnd: handleResponseEnd });
  
  const [ws, isConnected] = useWebSocket(wsUrl || "", { 
    onMessage: (event) => {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      handleMessage(event);
    },
    onCloseOrError: handleSocketError,
  });

  // Funzione per verificare se l'utente è vicino al fondo della pagina
  const isNearBottom = useCallback((threshold = 100) => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const distanceFromBottom = documentHeight - scrollTop - windowHeight;
    
    return distanceFromBottom <= threshold;
  }, []);


  

  // Funzione di scroll intelligente che rispetta l'interazione dell'utente
 const scrollToAbsoluteBottom = useCallback((force = false) => {
    // Se l'utente sta scrollando attivamente e non è forzato, non fare auto-scroll
    if (!force && isUserScrolling && !shouldAutoScroll) {
      return;
    }

    const scrollToBottom = () => {
      const body = document.body;
      const html = document.documentElement;
      
      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      );
      
      window.scrollTo({
        top: height,
        behavior: 'smooth'
      });
    };

    // Esegui immediatamente
    scrollToBottom();
    
    // Un singolo follow-up per assicurarsi di raggiungere il fondo
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [isUserScrolling, shouldAutoScroll]);



useEffect(() => {
  if (messages.length > previousMessageCount.current || isTyping || isStreaming) {
    if (shouldAutoScroll) {
      setTimeout(() => {
        scrollToAbsoluteBottom();
      }, 0);
    }
  }
  previousMessageCount.current = messages.length;
}, [messages, isTyping, isStreaming, shouldAutoScroll, scrollToAbsoluteBottom]);
  // Gestione intelligente dello scroll dell'utente
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollDelta = currentScrollY - lastScrollPositionRef.current;
    
    // Calcola la velocità di scroll
    scrollVelocityRef.current = Math.abs(scrollDelta);
    
    // Se l'utente sta scrollando attivamente
    if (scrollVelocityRef.current > 5) {
      setIsUserScrolling(true);
      
      // Se sta scrollando verso l'alto durante lo streaming, disabilita auto-scroll
      if (scrollDelta < 0 && isStreamingRef.current) {
        setShouldAutoScroll(false);
      }
      
      // Reset del timeout per rilevare quando smette di scrollare
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
      
      userScrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
        
        // Se l'utente ha scrollato fino in fondo, riabilita auto-scroll
        if (isNearBottom(50)) {
          setShouldAutoScroll(true);
          // Se stiamo streamando, fai scroll immediato
          if (isStreamingRef.current) {
            setTimeout(() => {
              scrollToAbsoluteBottom();
            }, 100);
          }
        }
      }, 500); // 500ms di inattività prima di considerare finito lo scroll
    }
    
    lastScrollPositionRef.current = currentScrollY;
    
    // Aggiorna la visibilità del FAB
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const distanceFromBottom = documentHeight - currentScrollY - windowHeight;
    setShowScrollFab(distanceFromBottom > 200);
    
  }, [isNearBottom, scrollToAbsoluteBottom]);

  // Effect per gestire lo scroll durante lo streaming
  useEffect(() => {
    isStreamingRef.current = isStreaming || isTyping || pendingResponses > 0;
  }, [isStreaming, isTyping, pendingResponses]);

  useEffect(() => {
    if (isConnected) {
      setShowErrorCard(false);
    }
  }, [isConnected]);

  useEffect(() => {
    const hasActiveResponse = pendingResponses > 0 || isStreaming || isTyping;
    
    if (hasActiveResponse) {
      setShowTypingIndicator(true);
      typingTimeoutRef.current && clearTimeout(typingTimeoutRef.current);
    } else {
      typingTimeoutRef.current = setTimeout(() => setShowTypingIndicator(false), 1000);
    }

    return () => {
      typingTimeoutRef.current && clearTimeout(typingTimeoutRef.current);
    };
  }, [pendingResponses, isStreaming, isTyping]);

  useEffect(() => {
    if (!userId || isVerifyingSession) return;

    const initialLoad = async () => {
      await loadMessages();
      setInitialLoading(false);
    };
    initialLoad();
  }, [userId, isVerifyingSession, loadMessages]);

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const justLoadedMore = useRef(false);
  const previousMessageCount = useRef(0);

  // Effect per l'auto-scroll intelligente dei messaggi
  useEffect(() => {
    if (justLoadedMore.current) {
      justLoadedMore.current = false;
      return;
    }

    // Se ci sono nuovi messaggi o stiamo scrivendo/streamando
    if (messages.length > previousMessageCount.current || isTyping || isStreaming) {
      // Scroll automatico solo se:
      // 1. Non è la prima volta che stiamo caricando i messaggi
      // 2. Dovremmo fare auto-scroll (l'utente non ha scrollato via)
      // 3. L'utente è vicino al fondo della pagina
      if (previousMessageCount.current > 0 && shouldAutoScroll) {
        scrollToAbsoluteBottom();
      } else if (previousMessageCount.current === 0) {
        // Prima carica, sempre scroll al fondo
        scrollToAbsoluteBottom();
      }
    }
    previousMessageCount.current = messages.length;
  }, [messages, isTyping, isStreaming, shouldAutoScroll, scrollToAbsoluteBottom]);

  // Event listener per lo scroll
  useEffect(() => {
    const throttledHandleScroll = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener("scroll", throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  // State for multi-select chips
  const [selectedMultiChips, setSelectedMultiChips] = useState<string[]>([]);
  const [initialMultiChips, setInitialMultiChips] = useState<string[]>([]);
  const lastProcessedMultiChipMsgId = useRef<string | number | null>(null);

  // Initialize selected chips from message payload
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.multiQuickReplies && lastMessage.id !== lastProcessedMultiChipMsgId.current) {
      const preSelected = lastMessage.multiQuickReplies
        .filter(chip => chip.is_selected)
        .map(chip => chip.title);
      
      setSelectedMultiChips(preSelected);
      setInitialMultiChips(preSelected);
      lastProcessedMultiChipMsgId.current = lastMessage.id;
    }
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent | string, backendPayload?: string) => {
    // Handle both event and direct string call
    let messageText = "";
    if (typeof e === "string") {
      messageText = e;
    } else {
      e.preventDefault();
      messageText = input;
    }

    // Calculate added and removed chips
    const addedChips = selectedMultiChips.filter(chip => !initialMultiChips.includes(chip));
    const removedChips = initialMultiChips.filter(chip => !selectedMultiChips.includes(chip));
    
    let chipsPayload = "";
    let chipsVisual = "";

    // Helper to remove emojis for payload
    const removeEmojis = (str: string) => {
      return str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '').trim();
    };

    // Special case: Remove all filters
    if (selectedMultiChips.length === 0 && initialMultiChips.length > 0) {
        chipsPayload = "Rimuovi tutti i filtri. ";
        chipsVisual = "➖ Rimuovi tutti i filtri";
    } else {
        // 1. Payload for the Agent (Technical commands)
        const addedPayload = addedChips.map(chip => `Aggiungi tra i filtri ${removeEmojis(chip)}`).join(". ");
        const removedPayload = removedChips.map(chip => `Rimuovi tra i filtri ${removeEmojis(chip)}`).join(". ");
        chipsPayload = [addedPayload, removedPayload].filter(Boolean).join(". ");

        // 2. Visual Message for the User (Friendly description)
        const visualParts: string[] = [];
        if (addedChips.length > 0) visualParts.push(`➕ Aggiunto: ${addedChips.join(", ")}`);
        if (removedChips.length > 0) visualParts.push(`➖ Rimosso: ${removedChips.join(", ")}`);
        chipsVisual = visualParts.join("\n");
    }

    if (messageText.trim() === "" && chipsPayload === "") return;

    let finalVisualMessage = messageText;
    let finalPayload = backendPayload || messageText;

    if (chipsPayload) {
      if (messageText) {
        finalVisualMessage = `${messageText}\n\n${chipsVisual}`;
        finalPayload = `${backendPayload || messageText}. ${chipsPayload}`;
      } else {
        finalVisualMessage = chipsVisual;
        finalPayload = chipsPayload;
      }
    }

    sendChatMessage(ws, finalVisualMessage, finalPayload);
    setInput("");
    // Update initial state to match current selection so we don't resend changes
    setInitialMultiChips([...selectedMultiChips]);
  };
  
  const shouldShowResponseIndicator = showTypingIndicator;

  const handleLoadMore = useCallback(async () => {
    justLoadedMore.current = true;
    await loadMoreMessages();
  }, [loadMoreMessages]);


const scrollCarousel = useCallback((index: number, dir: number) => {
  const el = carouselRefs.current[index];
  if (!el) return;

  const cardEl = el.querySelector(".MuiCard-root") as HTMLElement;
  const style = getComputedStyle(el);
  const gap = parseInt(style.columnGap || style.gap || "0");
  const cardWidth = (cardEl.offsetWidth || 280) + gap;

  const total = el.children.length;
  const currentIdx = Math.round(el.scrollLeft / cardWidth);
  const newIdx = Math.max(0, Math.min(currentIdx + dir, total - 1));

  const start = el.scrollLeft;
  const end = newIdx * cardWidth;
  const duration = 1; // 🔹 durata animazione in ms (più alto = più lento)
  const startTime = performance.now();

  const animate = (time: number) => {
    const elapsed = time - startTime;
    const progress = Math.min(elapsed / duration, 1);
    // easing (puoi cambiare funzione per effetto diverso)
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    el.scrollLeft = start + (end - start) * easedProgress;

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
}, []);


  const isChatEmpty = messages.length === 0;

  const handleFullReset = useCallback(async () => {
    await clearFirestoreMessages();
    setShouldAutoScroll(true); // Reset auto-scroll state
    setResetKey(prevKey => prevKey + 1);
  }, [clearFirestoreMessages]);

  const handleOpenClearConfirm = useCallback(() => {
    setConfirmClearOpen(true);
    setDropdownOpen(false);
  }, []);

  const handleConfirmClear = useCallback(async () => {
    setConfirmClearOpen(false);
    await handleFullReset();
  }, [handleFullReset]);

  // Gestione click del FAB per scroll al fondo
  const handleFabClick = useCallback(() => {
    setShouldAutoScroll(true);
    scrollToAbsoluteBottom();
  }, [scrollToAbsoluteBottom]);

  return (  
    <div className="App" key={resetKey}>
      <div className="chat-container" style={{ position: "relative" }}>
        <HeadBar
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          userId={userId || undefined}
          userDisplayName={userFullName || userEmail || undefined}
        />
        
        {/* Loading state while verifying session */}
        {isVerifyingSession && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1500,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={60} sx={{ color: "#098988" }} />
            <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
              🔐 Verifying session...
            </Typography>
          </Box>
        )}
        
        {showErrorCard && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1500,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Card
              sx={{
                width: 300,
                padding: 2,
                textAlign: "center",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(12px)",
                borderRadius: "16px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <CardContent>
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={{ color: theme.palette.text.primary }}
                >
                  Errore di Connessione
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ mt: 2, color: theme.palette.text.primary }}
                >
                  Impossibile connettersi al server. Per favore, riprova più tardi.
                </Typography>
              </CardContent>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
                sx={{
                  mt: 2,
                  backgroundColor: "#098988",
                  "&:hover": { backgroundColor: "#066b6a" },
                }}
              >
                OK
              </Button>
            </Card>
          </Box>
        )}
        {hasMore && (
          <div style={{ textAlign: "center", margin: "8px 0" }}>
            <Button variant="outlined" onClick={handleLoadMore}>
              Carica altri
            </Button>
          </div>
        )}
        <MessagesList
          messages={messages}
          isStreaming={isStreaming}
          isTyping={isTyping}
          bottomRef={bottomRef}
          sendMessage={handleSendMessage}
          userAvatar={userAvatar}
          botAvatar={botAvatar}
          scrollCarousel={scrollCarousel}
          carouselRefs={carouselRefs}
          messagesContainerRef={messagesContainerRef}
          isResponding={shouldShowResponseIndicator}
          isDarkMode={darkMode}
          isChatEmpty={isChatEmpty}
          setResponding={setIsResponding}
          initialLoading={initialLoading}
          setInput={setInput}
          selectedMultiChips={selectedMultiChips}
          setSelectedMultiChips={setSelectedMultiChips}
        />
        <ChatInput
          input={input}
          setInput={setInput}
          inputDisabled={inputDisabled || isResponding || guestLimitReached}
          sendMessage={handleSendMessage}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          dropdownRef={dropdownRef}
          onOpenClearConfirm={handleOpenClearConfirm}
          isDarkMode={darkMode}
          isResponding={isResponding}
          selectedMultiChips={selectedMultiChips}
          initialMultiChips={initialMultiChips}
          setSelectedMultiChips={setSelectedMultiChips}
        />
        {showScrollFab && (
          <Fab
            color="primary"
            size="small"
            onClick={handleFabClick}
            sx={{
              position: "fixed",
              bottom: 100,
              right: 20,
              zIndex: 1000,
              transition: "all 0.3s ease-in-out",
              // Aggiunge un indicatore visivo se l'auto-scroll è disabilitato durante lo streaming
              opacity: !shouldAutoScroll && isStreamingRef.current ? 0.8 : 1,
              animation: !shouldAutoScroll && isStreamingRef.current ? 'pulse 2s infinite' : 'none'
            }}
            aria-label="scroll to latest message"
          >
            <KeyboardDoubleArrowDownIcon />
          </Fab>
        )}
      </div>

      {/* Guest Limit Dialog */}
      <Dialog
        open={guestLimitReached}
        onClose={() => {}} // Prevent closing by clicking outside
        PaperProps={{
          sx: {
            width: 320,
            p: 2,
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }
        }}
      >
        <DialogTitle>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
          >
            Messaggi terminati
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="body1" 
            sx={{ mt: 2, mb: 1, color: theme.palette.text.primary }}
          >
            Hai raggiunto il limite di <strong>{GUEST_MESSAGE_LIMIT} messaggi</strong> come utente ospite.
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ color: theme.palette.text.secondary }}
          >
            Accedi o registrati per continuare a usare l'assistente senza limiti!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mt: 2 }}>
            <Button
              onClick={() => {
                const loginUrl = process.env.REACT_APP_LOGIN_URL || "https://boatsconnect.glueglue.dev/index";
                window.location.href = loginUrl;
              }}
              variant="contained"
              sx={{
                width: '100%',
                backgroundColor: darkMode ? '#098988' : '#ea8b3e',
                color: 'white',
                fontWeight: 600,
                "&:hover": { 
                  backgroundColor: darkMode ? '#087776' : '#d67a2e'
                },
                mb: 1,
              }}
            >
              Accedi / Registrati
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        PaperProps={{
          sx: {
          width: 300,
          p: 2, // shorthand di padding: 2
          textAlign: "center",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(12px)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
            
          }
        }}
      >
        <DialogTitle>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ color: theme.palette.text.primary }}
          >
            Sei sicuro?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="body2" 
            sx={{ mt: 2, color: theme.palette.text.primary }}
          >
            Questa operazione cancellerà tutti i messaggi dalla chat. Non potrà essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mt: 2 }}>
            <Button
              onClick={handleConfirmClear}
              autoFocus
              variant="contained"
              sx={{
                width: '100%',
                backgroundColor: "#098988",
                "&:hover": { backgroundColor: "#066b6a" },
                mb: 1, 
              }}
            >
              Svuota Chat
            </Button>
            <Button
              onClick={() => setConfirmClearOpen(false)}
              sx={{ color: theme.palette.text.secondary }}
            >
              Annulla
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default App;
